/**
 * Store Appointment Booking Modal
 * Handles appointment booking popup with day and time selection
 */

(function () {
    'use strict';

    // Parse working days string into structured data
    function parseWorkingDays(workingDaysString) {

        if (!workingDaysString || typeof workingDaysString !== 'string') {
            console.warn('[Appointment Booking] parseWorkingDays: Invalid input, returning empty object');
            return {};
        }

        const daysMap = {};
        const dayPatterns = {
            'MONDAY': 'monday',
            'TUESDAY': 'tuesday',
            'WEDNESDAY': 'wednesday',
            'THURSDAY': 'thursday',
            'FRIDAY': 'friday',
            'SATURDAY': 'saturday',
            'SUNDAY': 'sunday'
        };

        // Split by bullet separator (•)
        const dayEntries = workingDaysString.split('•').map(entry => entry.trim()).filter(Boolean);

        dayEntries.forEach(entry => {

            // Match pattern: "DAY: START_TIME - END_TIME"
            // Simplified regex that should match: "SATURDAY: 10:00AM - 12:00AM"
            // Pattern breakdown:
            // - Day name (case insensitive)
            // - Colon with optional spaces
            // - Time format: digits:digitsAM/PM (with optional spaces)
            // - Dash with spaces
            // - Time format: digits:digitsAM/PM (with optional spaces)
            const match = entry.match(/(MONDAY|TUESDAY|WEDNESDAY|THURSDAY|FRIDAY|SATURDAY|SUNDAY)\s*:\s*(\d{1,2}:\d{2}\s*(?:AM|PM))\s*-\s*(\d{1,2}:\d{2}\s*(?:AM|PM))/i);

            if (match) {
                const dayName = match[1].toUpperCase();
                // Normalize time format: remove spaces, ensure format is "HH:MMAM" or "HH:MMPM"
                let startTime = match[2].trim().replace(/\s+/g, '');
                let endTime = match[3].trim().replace(/\s+/g, '');
                const dayKey = dayPatterns[dayName];


                if (dayKey) {
                    daysMap[dayKey] = {
                        start: startTime,
                        end: endTime,
                        raw: entry
                    };
                } else {
                    console.error('[Appointment Booking] ✗ No dayKey found for:', dayName, 'Available keys:', Object.keys(dayPatterns));
                }
            } else {
                console.warn('[Appointment Booking] ✗ Entry did not match pattern:', entry);
                console.warn('[Appointment Booking] Entry characters:', Array.from(entry).map(c => c.charCodeAt(0)));

                // Try simpler pattern as fallback
                const simpleMatch = entry.match(/(SATURDAY|SUNDAY|MONDAY|TUESDAY|WEDNESDAY|THURSDAY|FRIDAY)[:\s]+(\d+:\d+(?:AM|PM))[-\s]+(\d+:\d+(?:AM|PM))/i);
                if (simpleMatch) {
                    const dayName = simpleMatch[1].toUpperCase();
                    let startTime = simpleMatch[2].trim().replace(/\s+/g, '');
                    let endTime = simpleMatch[3].trim().replace(/\s+/g, '');
                    const dayKey = dayPatterns[dayName];
                    if (dayKey) {
                        daysMap[dayKey] = {
                            start: startTime,
                            end: endTime,
                            raw: entry
                        };
                    }
                }
            }
        });
        return daysMap;
    }

    // Convert 12-hour time to 24-hour format
    function timeTo24Hour(time12h) {
        const [time, period] = time12h.toUpperCase().split(/(AM|PM)/);
        const [hours, minutes] = time.trim().split(':');
        let hour24 = parseInt(hours, 10);

        if (period === 'PM' && hour24 !== 12) {
            hour24 += 12;
        } else if (period === 'AM' && hour24 === 12) {
            hour24 = 0;
        }

        return { hours: hour24, minutes: parseInt(minutes, 10) || 0 };
    }

    // Convert 12-hour time to 24-hour format for end times (handles 12:00AM as 24:00)
    function timeTo24HourForEndTime(time12h) {
        const [time, period] = time12h.toUpperCase().split(/(AM|PM)/);
        const [hours, minutes] = time.trim().split(':');
        let hour24 = parseInt(hours, 10);
        const mins = parseInt(minutes, 10) || 0;

        // Special case: 12:00AM means end of day (24:00), not start of day (00:00)
        // This is for cases like "10:00AM - 12:00AM" meaning "until midnight"
        if (period === 'AM' && hour24 === 12 && mins === 0) {
            return { hours: 24, minutes: 0 };
        }

        // Normal conversion
        if (period === 'PM' && hour24 !== 12) {
            hour24 += 12;
        } else if (period === 'AM' && hour24 === 12) {
            hour24 = 0;
        }

        return { hours: hour24, minutes: mins };
    }

    // Convert 24-hour time to 12-hour format
    function timeTo12Hour(hours, minutes) {
        const period = hours >= 12 ? 'PM' : 'AM';
        let hour12 = hours % 12;
        if (hour12 === 0) hour12 = 12;
        const mins = minutes.toString().padStart(2, '0');
        return `${hour12}:${mins} ${period}`;
    }

    // Generate 30-minute time slots
    function generateTimeSlots(startTime, endTime) {
        const start = timeTo24Hour(startTime);
        // Use special function for end time to handle 12:00AM correctly
        const end = timeTo24HourForEndTime(endTime);

        const slots = [];
        let currentHour = start.hours;
        let currentMinute = start.minutes;

        // Handle case where end is 24:00 (midnight/end of day)
        const endHour = end.hours === 24 ? 24 : end.hours;
        const endMinute = end.hours === 24 ? 0 : end.minutes;

        while (currentHour < endHour || (currentHour === endHour && currentMinute < endMinute)) {
            // Don't add 24:00 slot, convert to 12:00AM instead
            if (currentHour === 24) {
                slots.push('12:00 AM');
                break;
            }

            slots.push(timeTo12Hour(currentHour, currentMinute));

            currentMinute += 30;
            if (currentMinute >= 60) {
                currentMinute = 0;
                currentHour++;
            }

            // Safety check to prevent infinite loop
            if (slots.length > 100) {
                console.warn('[Appointment Booking] Too many slots generated, stopping');
                break;
            }
        }
        return slots;
    }

    // Categorize time slots
    function categorizeTimeSlots(slots) {
        const morning = [];
        const noon = [];
        const evening = [];

        slots.forEach(slot => {
            const time24 = timeTo24Hour(slot);
            const hour24 = time24.hours;

            if (hour24 < 12) {
                morning.push(slot);
            } else if (hour24 >= 12 && hour24 < 16) {
                noon.push(slot);
            } else {
                evening.push(slot);
            }
        });

        return { morning, noon, evening };
    }

    // Get next 7 days
    function getNext7Days() {
        const days = [];
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

        for (let i = 0; i < 7; i++) {
            const date = new Date();
            date.setDate(date.getDate() + i);

            const dayIndex = date.getDay();
            const dayName = dayNames[dayIndex];
            const month = monthNames[date.getMonth()];
            const day = date.getDate();

            let label = '';
            if (i === 0) {
                label = 'Today';
            } else if (i === 1) {
                label = 'Tomorrow';
            } else {
                label = dayName;
            }

            days.push({
                date: date,
                label: label,
                display: `${label} - ${month} ${day}`,
                dayName: dayName.toLowerCase(),
                dateString: date.toISOString().split('T')[0]
            });
        }

        return days;
    }

    // Get next 7 days with valid timing (skip days without timing)
    function getNext7DaysWithTiming(workingDays) {
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const days = [];
        let dayOffset = 0;
        let dayCount = 0;

        // Keep adding days until we have 7 days with valid timing
        while (dayCount < 7 && dayOffset < 30) { // Max 30 days to prevent infinite loop
            const date = new Date();
            date.setDate(date.getDate() + dayOffset);

            const dayIndex = date.getDay();
            const dayName = dayNames[dayIndex];
            const dayNameLower = dayName.toLowerCase();
            const month = monthNames[date.getMonth()];
            const day = date.getDate();

            // Check if this day has valid timing
            const dayData = workingDays[dayNameLower];
            let hasValidTiming = false;


            if (dayData) {
                const start = dayData.start || dayData.startTime || '';
                const end = dayData.end || dayData.endTime || '';
                hasValidTiming = typeof start === 'string' && start.trim() !== '' &&
                    typeof end === 'string' && end.trim() !== '';
            } else {
            }

            // Only add days with valid timing
            if (hasValidTiming) {
                let label = '';
                if (dayOffset === 0) {
                    label = 'Today';
                } else if (dayOffset === 1) {
                    label = 'Tomorrow';
                } else {
                    label = dayName;
                }

                days.push({
                    date: date,
                    label: label,
                    display: `${label} - ${month} ${day}`,
                    dayName: dayNameLower,
                    dateString: date.toISOString().split('T')[0],
                    dayOffset: dayOffset
                });

                dayCount++;
            }

            dayOffset++;
        }

        return days;
    }

    // Format working days for display
    function formatWorkingDaysForDisplay(workingDays) {
        if (!workingDays || Object.keys(workingDays).length === 0) {
            return '';
        }

        const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
        const dayLabels = {
            'monday': 'Monday',
            'tuesday': 'Tuesday',
            'wednesday': 'Wednesday',
            'thursday': 'Thursday',
            'friday': 'Friday',
            'saturday': 'Saturday',
            'sunday': 'Sunday'
        };

        const formattedDays = dayOrder
            .filter(day => workingDays[day])
            .map(day => {
                const dayData = workingDays[day];
                return `${dayLabels[day]}: ${dayData.start} - ${dayData.end}`;
            });

        return formattedDays.join(' • ');
    }

    // Create appointment modal HTML
    function createAppointmentModal(storeName, workingDays, storeTimingDisplay) {
        const modalId = 'store-appointment-modal';
        let existingModal = document.getElementById(modalId);
        if (existingModal) {
            existingModal.remove();
        }

        const modal = document.createElement('div');
        modal.id = modalId;
        modal.className = 'store-appointment-modal';

        // Get consent checkbox label from template if available
        const consentTemplate = document.getElementById('appointment-consent-checkbox-template');
        let consentLabel = 'I agree to use my details to arrange and manage an appointment.';
        if (consentTemplate) {
            const consentSpan = consentTemplate.querySelector('span');
            if (consentSpan) {
                consentLabel = consentSpan.textContent.trim();
            }
        }

        modal.innerHTML = `
        <div class="store-appointment-modal-overlay"></div>
        <div class="store-appointment-modal-content">
          <button class="store-appointment-modal-close" aria-label="Close">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          
          <!-- Day/Time Selection Section -->
          <div class="store-appointment-selection-section" id="appointment-selection-section">
            <h2 class="store-appointment-modal-title">Heading to Our Store?</h2>
            <p class="store-appointment-modal-subtitle">Let us make it smoother and tailored for you. Book an appointment to skip the wait.</p>
            
            <div class="store-appointment-controls">
              <button class="store-appointment-btn-day" id="appointment-choose-day">
                <span>Choose Day</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
                  <path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
              
              <button class="store-appointment-btn-time" id="appointment-choose-time" disabled>
                <span>Choose Time</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
                  <path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>
  
            <div class="store-appointment-day-dropdown" id="appointment-day-dropdown" style="display: none;"></div>
            <div class="store-appointment-time-dropdown" id="appointment-time-dropdown" style="display: none;"></div>
            
            <button class="store-appointment-btn-proceed" id="appointment-proceed" disabled>
              <span>Proceed</span>
            </button>
          </div>
  
          <!-- Booking Form Section (Hidden Initially) -->
          <div class="store-appointment-form-section" id="appointment-form-section" style="display: none;">
            <h2 class="store-appointment-modal-title">You're Almost There!</h2>
            <form id="appointment-booking-form" class="appointment-booking-form">
              <div class="appointment-form-row">
                <div class="appointment-form-field">
                  <input type="text" id="appointment-name" name="name" placeholder="Name*" required>
                </div>
                <div class="appointment-form-field">
                  <input type="tel" id="appointment-phone" pattern="[0-9]{12}" maxlength="12" oninput="this.value = this.value.replace(/[^0-9]/g, '')" name="phone" placeholder="Phone Number*" required>
                </div>
              </div>
              <div class="appointment-form-row">
                <div class="appointment-form-field appointment-form-field-email">
                  <input type="email" id="appointment-email" name="email" placeholder="Type Your Email ID*" required>
                </div>
                <button type="submit" class="appointment-book-now-btn" id="appointment-book-now">
                  <span>Book Now</span>
                </button>
              </div>
              <div class="appointment-consent-checkbox-container">
                <label class="appointment-consent-checkbox-label">
                  <input type="checkbox" id="appointment-consent" name="appointment_consent" required>
                  <span>${consentLabel}</span>
                </label>
              </div>
              <label class="booking_consent-text">
                    By continuing. I agree to
                      <a href="/pages/terms-conditions" target="_blank" style="display: inline; font-size: 12px; text-decoration: underline;">Terms and Conditions</a> &amp;
                      <a href="/pages/privacy-notice" target="_blank" style="display: inline; font-size: 12px; text-decoration: underline;">Privacy Notice</a>
                    
                </label>
            </form>
            <p class="success-message" id="appointment-msg-wrapper">Submit Successfully</p>
          </div>
        </div>
      `;

        document.body.appendChild(modal);
        return modal;
    }

    // Render day dropdown
    function renderDayDropdown(days, selectedDay, workingDays) {
        const dropdown = document.getElementById('appointment-day-dropdown');
        if (!dropdown) return;

        dropdown.innerHTML = '';

        // All days passed to this function already have valid timing (filtered by getNext7DaysWithTiming)
        days.forEach(day => {
            const dayItem = document.createElement('div');
            dayItem.className = `store-appointment-day-item ${selectedDay === day.dateString ? 'selected' : ''}`;
            dayItem.dataset.date = day.dateString;
            dayItem.dataset.dayName = day.dayName;

            dayItem.innerHTML = `
          <span class="day-label">${day.label}</span>
          <span class="day-date">${day.display.split(' - ')[1]}</span>
        `;

            dayItem.addEventListener('click', () => {
                selectDay(day, workingDays);
                // Close dropdown after selection
                const dayDropdown = document.getElementById('appointment-day-dropdown');
                if (dayDropdown) {
                    dayDropdown.style.display = 'none';
                }
            });

            dropdown.appendChild(dayItem);
        });
    }

    // Render time dropdown
    function renderTimeDropdown(selectedDay, workingDays) {
        const dropdown = document.getElementById('appointment-time-dropdown');
        const timeBtn = document.getElementById('appointment-choose-time');

        if (!dropdown) {
            console.error('[Appointment Booking] Time dropdown element not found');
            return;
        }
        if (!timeBtn) {
            console.error('[Appointment Booking] Time button element not found');
            return;
        }

        const dayData = workingDays[selectedDay.dayName];
        if (!dayData) {
            console.warn('[Appointment Booking] No working hours for:', selectedDay.dayName);
            dropdown.style.display = 'none';
            dropdown.innerHTML = '<div style="padding: 20px; text-align: center; color: #8a6e63; font-family: Poppins, sans-serif;">No time slots available for this day</div>';
            return;
        }

        const slots = generateTimeSlots(dayData.start, dayData.end);
        const categorized = categorizeTimeSlots(slots);

        // Determine which tab to show first (prefer Noon, then Evening, then Morning)
        let activeTab = 'noon';
        if (categorized.noon.length === 0 && categorized.evening.length > 0) {
            activeTab = 'evening';
        } else if (categorized.noon.length === 0 && categorized.evening.length === 0 && categorized.morning.length > 0) {
            activeTab = 'morning';
        }

        dropdown.innerHTML = `
        <div class="store-appointment-time-wrapper">
          <div class="store-appointment-time-tabs">
            ${categorized.morning.length > 0 ? `
              <button class="time-tab ${activeTab === 'morning' ? 'active' : ''}" data-tab="morning">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">
                  <path d="M10 2C5.58 2 2 5.58 2 10C2 14.42 5.58 18 10 18C14.42 18 18 14.42 18 10C18 5.58 14.42 2 10 2ZM10 16C6.69 16 4 13.31 4 10C4 6.69 6.69 4 10 4C13.31 4 16 6.69 16 10C16 13.31 13.31 16 10 16ZM9.5 5H10.5V10.25L14 11.92L13.25 12.92L9.5 11V5Z" fill="currentColor"/>
                </svg>
                <span>Morning</span>
              </button>
            ` : ''}
            ${categorized.noon.length > 0 ? `
              <button class="time-tab ${activeTab === 'noon' ? 'active' : ''}" data-tab="noon">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">
                  <path d="M10 2C5.58 2 2 5.58 2 10C2 14.42 5.58 18 10 18C14.42 18 18 14.42 18 10C18 5.58 14.42 2 10 2ZM10 16C6.69 16 4 13.31 4 10C4 6.69 6.69 4 10 4C13.31 4 16 6.69 16 10C16 13.31 13.31 16 10 16ZM9.5 5H10.5V10.25L14 11.92L13.25 12.92L9.5 11V5Z" fill="currentColor"/>
                </svg>
                <span>Noon</span>
              </button>
            ` : ''}
            ${categorized.evening.length > 0 ? `
              <button class="time-tab ${activeTab === 'evening' ? 'active' : ''}" data-tab="evening">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">
                  <path d="M10 2C5.58 2 2 5.58 2 10C2 14.42 5.58 18 10 18C14.42 18 18 14.42 18 10C18 5.58 14.42 2 10 2ZM10 16C6.69 16 4 13.31 4 10C4 6.69 6.69 4 10 4C13.31 4 16 6.69 16 10C16 13.31 13.31 16 10 16ZM9.5 5H10.5V10.25L14 11.92L13.25 12.92L9.5 11V5Z" fill="currentColor"/>
                </svg>
                <span>Evening</span>
              </button>
            ` : ''}
          </div>
          <div class="store-appointment-time-content">
            ${categorized.morning.length > 0 ? `
              <div class="time-tab-content ${activeTab === 'morning' ? 'active' : ''}" data-content="morning">
                <div class="time-slots">
                  ${categorized.morning.map(slot => `<button class="time-slot" data-time="${slot}">${slot}</button>`).join('')}
                </div>
              </div>
            ` : ''}
            ${categorized.noon.length > 0 ? `
              <div class="time-tab-content ${activeTab === 'noon' ? 'active' : ''}" data-content="noon">
                <div class="time-slots">
                  ${categorized.noon.map(slot => `<button class="time-slot" data-time="${slot}">${slot}</button>`).join('')}
                </div>
              </div>
            ` : ''}
            ${categorized.evening.length > 0 ? `
              <div class="time-tab-content ${activeTab === 'evening' ? 'active' : ''}" data-content="evening">
                <div class="time-slots">
                  ${categorized.evening.map(slot => `<button class="time-slot" data-time="${slot}">${slot}</button>`).join('')}
                </div>
              </div>
            ` : ''}
          </div>
        </div>
      `;

        // Add tab switching functionality
        dropdown.querySelectorAll('.time-tab').forEach(tab => {
            tab.addEventListener('click', function () {
                const tabName = this.dataset.tab;

                // Update tab active state
                dropdown.querySelectorAll('.time-tab').forEach(t => t.classList.remove('active'));
                this.classList.add('active');

                // Update content visibility
                dropdown.querySelectorAll('.time-tab-content').forEach(content => {
                    if (content.dataset.content === tabName) {
                        content.classList.add('active');
                    } else {
                        content.classList.remove('active');
                    }
                });
            });
        });

        // Add click handlers for time slots
        dropdown.querySelectorAll('.time-slot').forEach(btn => {
            btn.addEventListener('click', function () {
                dropdown.querySelectorAll('.time-slot').forEach(b => b.classList.remove('selected'));
                this.classList.add('selected');
                const selectedTime = this.dataset.time;
                updateTimeButton(selectedTime);
                enableProceedButton(); // This will check both time and consent
                // Close time dropdown after selection
                const timeDropdown = document.getElementById('appointment-time-dropdown');
                if (timeDropdown) {
                    timeDropdown.style.display = 'none';
                }
            });
        });
    }

    let selectedDayData = null;
    let selectedTime = null;

    function selectDay(day, workingDays) {

        // Check if the selected day has working hours/time slots
        const dayData = workingDays[day.dayName];
        if (!dayData) {
            console.warn('[Appointment Booking] No working hours for selected day:', day.dayName);
            alert('This day is closed. Please select another day.');
            return; // Don't proceed with selection
        }

        // Validate that dayData has valid start and end times
        const start = dayData.start || dayData.startTime || '';
        const end = dayData.end || dayData.endTime || '';
        if (!start || !end || start.trim() === '' || end.trim() === '') {
            console.warn('[Appointment Booking] Invalid or empty timing for selected day:', day.dayName, { start, end });
            alert('This day is closed. Please select another day.');
            return; // Don't proceed with selection
        }

        selectedDayData = day;
        selectedTime = null;

        const dayBtn = document.getElementById('appointment-choose-day');
        const timeBtn = document.getElementById('appointment-choose-time');
        const dayDropdown = document.getElementById('appointment-day-dropdown');
        const timeDropdown = document.getElementById('appointment-time-dropdown');
        const proceedBtn = document.getElementById('appointment-proceed');

        if (dayBtn && day) {
            const span = dayBtn.querySelector('span');
            if (span) {
                span.textContent = day.display;
            }
            dayBtn.classList.add('selected');
        }

        if (dayDropdown) {
            dayDropdown.style.display = 'none';
        }

        if (timeBtn) {
            timeBtn.disabled = false;
            timeBtn.classList.remove('selected');
            const timeSpan = timeBtn.querySelector('span');
            if (timeSpan) {
                timeSpan.textContent = 'Choose Time';
            }
        }

        // Render time dropdown immediately - but don't show it yet
        if (timeDropdown) {
            renderTimeDropdown(day, workingDays);
            timeDropdown.style.display = 'none'; // Keep hidden until user clicks "Choose Time"
        } else {
            console.error('[Appointment Booking] Time dropdown element not found!');
        }

        if (proceedBtn) {
            proceedBtn.disabled = true;
        }
    }

    function updateTimeButton(time) {
        selectedTime = time;
        const timeBtn = document.getElementById('appointment-choose-time');
        if (timeBtn && time) {
            timeBtn.querySelector('span').textContent = time;
            timeBtn.classList.add('selected');
        }
    }

    function enableProceedButton() {
        const proceedBtn = document.getElementById('appointment-proceed');

        // Enable button when both day and time are selected (consent will be checked on proceed click)
        if (proceedBtn && selectedDayData && selectedTime) {
            proceedBtn.disabled = false;
        } else {
            if (proceedBtn) {
                proceedBtn.disabled = true;
            }
        }
    }


    // Get working days from store detail page metaobjects
    function getWorkingDaysFromDetailPage() {
        try {
            const metaobjectsScript = document.getElementById('titan-store-metaobjects');
            if (!metaobjectsScript) {
                console.warn('[Appointment Booking] Metaobjects script not found');
                return '';
            }

            const metaobjects = JSON.parse(metaobjectsScript.textContent);

            const detailSection = document.getElementById('store-detail-section');
            if (!detailSection) {
                console.warn('[Appointment Booking] Store detail section not found');
                return '';
            }

            // Try to get store code from URL
            const urlParams = new URLSearchParams(window.location.search);
            const urlStoreCode = urlParams.get('store');

            // Try to get store code from sessionStorage (saved by store-places-api.js)
            let sessionStoreCode = null;
            try {
                const storedData = sessionStorage.getItem('titanSelectedStore');
                if (storedData) {
                    const parsed = JSON.parse(storedData);
                    sessionStoreCode = parsed.code;
                }
            } catch (e) {
                console.warn('[Appointment Booking] Could not read sessionStorage:', e);
            }

            // Try to get store name from page
            const storeNameEl = document.querySelector('[data-store-detail="title"]');
            const storeName = storeNameEl ? storeNameEl.textContent.trim() : '';

            const storeCode = urlStoreCode || sessionStoreCode || detailSection.dataset.defaultStoreCode;

            let store = null;

            // First try: match by store code
            if (storeCode) {
                store = metaobjects.find(s => {
                    const code1 = s.store_code || s.storeCode || '';
                    const code2 = s.store_code?.value || s.storeCode?.value || '';
                    return (code1 && code1.toLowerCase() === storeCode.toLowerCase()) ||
                        (code2 && code2.toLowerCase() === storeCode.toLowerCase());
                });
            }

            // Second try: match by store name
            if (!store && storeName) {
                store = metaobjects.find(s => {
                    const name1 = s.store_name || s.name || '';
                    const name2 = s.store_name?.value || s.name?.value || '';
                    return (name1 && name1.toLowerCase() === storeName.toLowerCase()) ||
                        (name2 && name2.toLowerCase() === storeName.toLowerCase());
                });
            }

            if (store) {

                const workingDays = store.working_days_and_slot_timings ||
                    store.working_days_and_slot_timings?.value ||
                    store.working_days ||
                    store.working_days?.value;

                if (workingDays) {
                    return workingDays;
                } else {
                    console.warn('[Appointment Booking] Store found but no working days data');
                }
            } else {
                console.warn('[Appointment Booking] Store not found in metaobjects');
            }

            // Fallback: get first store if available
            if (metaobjects.length > 0) {
                const firstStore = metaobjects[0];
                const fallbackWorkingDays = firstStore.working_days_and_slot_timings ||
                    firstStore.working_days_and_slot_timings?.value ||
                    firstStore.working_days ||
                    firstStore.working_days?.value;
                if (fallbackWorkingDays) {
                    return fallbackWorkingDays;
                }
            }
        } catch (error) {
            console.error('[Appointment Booking] Error getting working days from metaobjects:', error);
        }
        return '';
    }

    // Initialize appointment booking
    function initAppointmentBooking() {

        // Get all appointment buttons and attach click handlers directly
        const buttons = document.querySelectorAll('.store-appointment-btn');

        buttons.forEach(function (btn, index) {
            // Attach multiple handlers to ensure it works
            // Handler 1: Direct click
            btn.onclick = function (e) {
                e.preventDefault();
                e.stopPropagation();
                handleAppointmentClick(this, e);
                return false;
            };

            // Handler 2: addEventListener with capture
            btn.addEventListener('click', function (e) {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                handleAppointmentClick(this, e);
                return false;
            }, true);

            // Handler 3: addEventListener without capture
            btn.addEventListener('click', function (e) {
                e.preventDefault();
                e.stopPropagation();
                handleAppointmentClick(this, e);
                return false;
            }, false);
        });

        // Helper function to handle the click
        function handleAppointmentClick(btn, e) {
            // Get store index from button ID (could be store-appointment-X or store-directions-X)
            const storeIndex = btn.dataset.storeIndex ||
                btn.id.replace('store-appointment-', '').replace('store-directions-', '');
            const storeCard = document.querySelector(`[data-store-index="${storeIndex}"]`);

            let storeName = 'Store';
            let storeCode = '';
            let workingDaysStr = '';

            if (storeCard) {
                // From store list page
                storeName = storeCard.dataset.storeName || btn.dataset.storeName || 'Store';
                storeCode = storeCard.dataset.storeCode || btn.dataset.storeCode || '';
                workingDaysStr = storeCard.dataset.storeWorkingDays || btn.dataset.storeWorkingDays || '';

                // If store code is still empty, try to get from sessionStorage
                if (!storeCode || !storeCode.trim()) {
                    try {
                        const storedData = sessionStorage.getItem('titanSelectedStore');
                        if (storedData) {
                            const parsed = JSON.parse(storedData);
                            storeCode = parsed.code || '';
                        }
                    } catch (e) {
                        console.warn('[Appointment Booking] Could not read store code from sessionStorage:', e);
                    }
                }

            } else {
                // From store detail page
                storeName = document.querySelector('[data-store-detail="title"]')?.textContent || 'Store';
                workingDaysStr = getWorkingDaysFromDetailPage();
                // Try to get store code from URL or sessionStorage
                const urlParams = new URLSearchParams(window.location.search);
                storeCode = urlParams.get('store') || '';

                // If still empty, try sessionStorage
                if (!storeCode || !storeCode.trim()) {
                    try {
                        const storedData = sessionStorage.getItem('titanSelectedStore');
                        if (storedData) {
                            const parsed = JSON.parse(storedData);
                            storeCode = parsed.code || '';
                        }
                    } catch (e) {
                        console.warn('[Appointment Booking] Could not read store code from sessionStorage:', e);
                    }
                }
            }

            openAppointmentModal(storeName, workingDaysStr, storeCode);
        }

        // Also use event delegation as backup
        document.addEventListener('click', function (e) {
            const appointmentBtn = e.target.closest('.store-appointment-btn');
            if (!appointmentBtn) return;

            // Check if this is already handled
            if (appointmentBtn.dataset.handled === 'true') return;

            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();

            const storeIndex = appointmentBtn.dataset.storeIndex || appointmentBtn.id.replace('store-directions-', '');
            const storeCard = document.querySelector(`[data-store-index="${storeIndex}"]`);

            let storeName = 'Store';
            let storeCode = '';
            let workingDaysStr = '';

            if (storeCard) {
                storeName = storeCard.dataset.storeName || appointmentBtn.dataset.storeName || 'Store';
                storeCode = storeCard.dataset.storeCode || appointmentBtn.dataset.storeCode || '';
                workingDaysStr = storeCard.dataset.storeWorkingDays || appointmentBtn.dataset.storeWorkingDays || '';

                // If store code is still empty, try to get from sessionStorage
                if (!storeCode || !storeCode.trim()) {
                    try {
                        const storedData = sessionStorage.getItem('titanSelectedStore');
                        if (storedData) {
                            const parsed = JSON.parse(storedData);
                            storeCode = parsed.code || '';
                        }
                    } catch (e) {
                        console.warn('[Appointment Booking] Could not read store code from sessionStorage:', e);
                    }
                }
            } else {
                storeName = document.querySelector('[data-store-detail="title"]')?.textContent || 'Store';
                workingDaysStr = getWorkingDaysFromDetailPage();
                // Try to get store code from URL or sessionStorage
                const urlParams = new URLSearchParams(window.location.search);
                storeCode = urlParams.get('store') || '';

                // If still empty, try sessionStorage
                if (!storeCode || !storeCode.trim()) {
                    try {
                        const storedData = sessionStorage.getItem('titanSelectedStore');
                        if (storedData) {
                            const parsed = JSON.parse(storedData);
                            storeCode = parsed.code || '';
                        }
                    } catch (e) {
                        console.warn('[Appointment Booking] Could not read store code from sessionStorage:', e);
                    }
                }
            }

            appointmentBtn.dataset.handled = 'true';
            openAppointmentModal(storeName, workingDaysStr, storeCode);
        }, true);
    }

    function openAppointmentModal(storeName, workingDaysString, storeCode) {

        let modal;
        let parsedWorkingDays = {};

        try {
            // Handle working days - could be array, JSON array string, or bullet-separated string
            let workingDaysStr = '';

            if (Array.isArray(workingDaysString)) {
                // Already an array, convert to bullet-separated format
                workingDaysStr = workingDaysString.join(' • ');
            } else if (typeof workingDaysString === 'string') {
                if (workingDaysString.trim().startsWith('[')) {
                    // It's a JSON array string, parse and convert to bullet-separated format
                    try {
                        const daysArray = JSON.parse(workingDaysString);
                        if (Array.isArray(daysArray)) {
                            workingDaysStr = daysArray.join(' • ');
                        } else {
                            workingDaysStr = workingDaysString;
                        }
                    } catch (e) {
                        console.warn('[Appointment Booking] Could not parse JSON, using as-is');
                        workingDaysStr = workingDaysString;
                    }
                } else {
                    // Already a bullet-separated string
                    workingDaysStr = workingDaysString;
                }
            } else {
                console.warn('[Appointment Booking] Working days is not array or string:', typeof workingDaysString);
                workingDaysStr = '';
            }

            parsedWorkingDays = parseWorkingDays(workingDaysStr);

            // Format working days for display
            const storeTimingDisplay = formatWorkingDaysForDisplay(parsedWorkingDays);

            // Get only days that have valid timing (skip days without timing)
            const days = getNext7DaysWithTiming(parsedWorkingDays);

            selectedDayData = null;
            selectedTime = null;

            modal = createAppointmentModal(storeName, parsedWorkingDays, storeTimingDisplay);

            // Store store code and name in modal for later use
            if (modal) {
                const trimmedStoreCode = (storeCode || '').trim();
                modal.dataset.storeCode = trimmedStoreCode;
                modal.dataset.storeName = (storeName || '').trim();

                if (!trimmedStoreCode) {
                    console.warn('[Appointment Booking] WARNING: Store code is empty when opening modal!');
                }
            }

            renderDayDropdown(days, null, parsedWorkingDays);

            modal.style.display = 'flex';

            // Prevent body scroll when modal is open
            document.body.style.overflow = 'hidden';
        } catch (error) {
            // Restore body scroll in case of error
            document.body.style.overflow = '';
            alert('Error opening appointment modal. Check console for details.');
            return;
        }

        if (!modal) {
            console.error('[Appointment Booking] Modal was not created!');
            return;
        }

        // Store parsed working days for later use
        modal.dataset.workingDays = JSON.stringify(parsedWorkingDays);

        // Day button click
        const dayBtn = document.getElementById('appointment-choose-day');
        const dayDropdown = document.getElementById('appointment-day-dropdown');

        if (dayBtn && dayDropdown) {
            // Position dropdown relative to viewport
            function positionDayDropdown() {
                const btnRect = dayBtn.getBoundingClientRect();
                const viewportHeight = window.innerHeight;
                const viewportWidth = window.innerWidth;
                const isMobile = window.innerWidth <= 768;

                // Get actual dropdown height after it's rendered, or use estimate
                let dropdownHeight = 400; // Default estimate
                if (dayDropdown.offsetHeight > 0) {
                    dropdownHeight = dayDropdown.offsetHeight;
                } else {
                    // Estimate based on mobile vs desktop
                    dropdownHeight = isMobile ? 300 : 400;
                }

                // Position relative to viewport, not modal
                dayDropdown.style.position = 'fixed';
                dayDropdown.style.left = btnRect.left + 'px';
                dayDropdown.style.width = btnRect.width + 'px';
                dayDropdown.style.right = 'auto';
                dayDropdown.style.bottom = 'auto'; // Reset bottom

                const spaceAbove = btnRect.top;
                const spaceBelow = viewportHeight - btnRect.bottom;

                // On mobile, ALWAYS open below the button (unless absolutely no space)
                if (isMobile) {
                    // Mobile: Force open below, only use above if there's literally no space below
                    const minSpaceNeeded = 200; // Minimum space needed for dropdown
                    if (spaceBelow >= minSpaceNeeded) {
                        // Enough space below - open below
                        dayDropdown.style.top = (btnRect.bottom + 8) + 'px';
                        dayDropdown.style.bottom = 'auto';
                    } else if (spaceBelow < 50 && spaceAbove > spaceBelow) {
                        // Very little space below, but more above - open above
                        dayDropdown.style.top = (btnRect.top - Math.min(dropdownHeight, spaceAbove - 20)) + 'px';
                        dayDropdown.style.bottom = 'auto';
                    } else {
                        // Default: open below even if cramped
                        dayDropdown.style.top = (btnRect.bottom + 8) + 'px';
                        dayDropdown.style.bottom = 'auto';
                        // Adjust max-height if needed to fit
                        dayDropdown.style.maxHeight = spaceBelow + 'px';
                    }
                } else {
                    // Desktop: Original behavior - prefer opening above the button/popup
                    if (spaceAbove >= dropdownHeight) {
                        // Enough space above - open above
                        dayDropdown.style.top = (btnRect.top - dropdownHeight - 8) + 'px';
                        dayDropdown.style.bottom = 'auto';
                    } else {
                        // Not enough space above - open below
                        dayDropdown.style.top = (btnRect.bottom + 8) + 'px';
                        dayDropdown.style.bottom = 'auto';
                    }
                }

                // Ensure dropdown doesn't go off screen horizontally
                const dropdownRight = btnRect.left + btnRect.width;
                if (dropdownRight > viewportWidth - 20) {
                    dayDropdown.style.left = (viewportWidth - btnRect.width - 20) + 'px';
                }
                if (btnRect.left < 20) {
                    dayDropdown.style.left = '20px';
                }

                // On mobile, ensure dropdown width matches button width
                if (isMobile) {
                    dayDropdown.style.width = btnRect.width + 'px';
                    dayDropdown.style.left = btnRect.left + 'px';
                }
            }

            dayBtn.addEventListener('click', function (e) {
                e.stopPropagation();
                const isOpen = dayDropdown.style.display !== 'none' && dayDropdown.style.display !== '';
                if (isOpen) {
                    dayDropdown.style.display = 'none';
                } else {
                    // Temporarily show dropdown off-screen to get accurate dimensions
                    dayDropdown.style.display = 'block';
                    dayDropdown.style.visibility = 'hidden';
                    dayDropdown.style.top = '-9999px';
                    // Use requestAnimationFrame to ensure DOM is updated before positioning
                    requestAnimationFrame(() => {
                        positionDayDropdown();
                        dayDropdown.style.visibility = 'visible';
                    });
                }
                document.getElementById('appointment-time-dropdown').style.display = 'none';
            });

            // Reposition on window resize
            window.addEventListener('resize', function () {
                if (dayDropdown.style.display !== 'none' && dayDropdown.style.display !== '') {
                    positionDayDropdown();
                }
            });
        }

        // Time button click
        const timeBtn = document.getElementById('appointment-choose-time');
        const timeDropdown = document.getElementById('appointment-time-dropdown');

        if (timeBtn && timeDropdown) {
            // Position dropdown relative to viewport
            function positionTimeDropdown() {
                // Recalculate button position each time (in case modal moved)
                const btnRect = timeBtn.getBoundingClientRect();
                const viewportHeight = window.innerHeight;
                const viewportWidth = window.innerWidth;
                const isMobile = window.innerWidth <= 768;

                // Get actual dropdown height after it's rendered
                let dropdownHeight = 500; // Default estimate
                if (timeDropdown.offsetHeight > 0) {
                    dropdownHeight = timeDropdown.offsetHeight;
                } else {
                    // Estimate based on mobile vs desktop
                    dropdownHeight = isMobile ? 350 : 500;
                }

                // Clear any previous positioning and reset styles
                timeDropdown.style.position = 'fixed';
                timeDropdown.style.right = 'auto';
                timeDropdown.style.bottom = 'auto';
                timeDropdown.style.top = 'auto';
                timeDropdown.style.left = 'auto';
                timeDropdown.style.width = 'auto';
                timeDropdown.style.maxHeight = '';
                // Force reflow to ensure styles are applied
                timeDropdown.offsetHeight;

                // Calculate available space
                const spaceAbove = btnRect.top;
                const spaceBelow = viewportHeight - btnRect.bottom;

                // Always open below the button (same as day selection)
                timeDropdown.style.top = (btnRect.bottom + 8) + 'px';
                timeDropdown.style.left = btnRect.left + 'px';
                timeDropdown.style.width = btnRect.width + 'px';

                // If not enough space below, adjust max-height to fit
                if (spaceBelow < dropdownHeight) {
                    timeDropdown.style.maxHeight = Math.max(200, spaceBelow - 20) + 'px';
                } else {
                    // Reset max-height if there's enough space
                    timeDropdown.style.maxHeight = '';
                }


                // Ensure dropdown doesn't go off screen horizontally
                const dropdownLeft = parseFloat(timeDropdown.style.left) || btnRect.left;
                const dropdownWidth = parseFloat(timeDropdown.style.width) || btnRect.width;
                const dropdownRight = dropdownLeft + dropdownWidth;

                if (dropdownRight > viewportWidth - 20) {
                    timeDropdown.style.left = (viewportWidth - dropdownWidth - 20) + 'px';
                }
                if (dropdownLeft < 20) {
                    timeDropdown.style.left = '20px';
                }
            }

            timeBtn.addEventListener('click', function (e) {
                e.stopPropagation();
                e.preventDefault();

                if (!selectedDayData) {
                    alert('Please select a day first');
                    return;
                }

                const dayDropdown = document.getElementById('appointment-day-dropdown');
                if (dayDropdown) {
                    dayDropdown.style.display = 'none';
                }

                const isOpen = timeDropdown.style.display !== 'none' && timeDropdown.style.display !== '';

                if (isOpen) {
                    timeDropdown.style.display = 'none';
                } else {
                    // Ensure time dropdown is rendered - get working days from modal or re-parse
                    if (!timeDropdown.querySelector('.time-slot')) {
                        const modal = document.getElementById('store-appointment-modal');
                        let workingDays = {};
                        if (modal && modal.dataset.workingDays) {
                            try {
                                workingDays = JSON.parse(modal.dataset.workingDays);
                            } catch (e) {
                                console.warn('[Appointment Booking] Could not parse stored working days');
                            }
                        }
                        if (Object.keys(workingDays).length === 0) {
                            // Fallback: try to get from original data
                            const storeCard = document.querySelector(`[data-store-index="${selectedDayData ? selectedDayData.storeIndex : ''}"]`);
                            if (storeCard) {
                                const workingDaysStr = storeCard.dataset.storeWorkingDays || '';
                                workingDays = parseWorkingDays(workingDaysStr);
                            }
                        }
                        renderTimeDropdown(selectedDayData, workingDays);
                    }
                    // Temporarily show dropdown off-screen to get accurate dimensions
                    timeDropdown.style.display = 'block';
                    timeDropdown.style.visibility = 'hidden';
                    timeDropdown.style.position = 'fixed';
                    timeDropdown.style.top = '-9999px';
                    timeDropdown.style.left = '0';
                    // Use double requestAnimationFrame to ensure DOM is fully updated
                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                            positionTimeDropdown();
                            timeDropdown.style.visibility = 'visible';
                        });
                    });
                }
            });

            // Reposition on window resize
            window.addEventListener('resize', function () {
                if (timeDropdown.style.display !== 'none' && timeDropdown.style.display !== '') {
                    positionTimeDropdown();
                }
            });
        }

        // Proceed button
        const proceedBtn = document.getElementById('appointment-proceed');
        if (proceedBtn) {
            proceedBtn.addEventListener('click', function () {

                if (!selectedDayData || !selectedTime) {
                    console.warn('[Appointment Booking] Day or time not selected');
                    return;
                }

                // Hide selection section (title, subtitle, controls, dropdowns)
                const selectionSection = document.getElementById('appointment-selection-section');
                if (selectionSection) {
                    selectionSection.style.display = 'none';
                }

                // Show form section
                const formSection = document.getElementById('appointment-form-section');
                if (formSection) {
                    formSection.style.display = 'block';
                }
            });
        }

        // Helper function to get store info
        function getStoreInfo() {
            try {
                // First, try to get from modal (most reliable when form is being submitted)
                const modal = document.getElementById('store-appointment-modal');
                if (modal && modal.dataset.storeCode) {
                    const modalStoreCode = modal.dataset.storeCode.trim();
                    const modalStoreName = modal.dataset.storeName || storeName || '';
                    if (modalStoreCode) {
                        return {
                            storeCode: modalStoreCode,
                            storeName: modalStoreName
                        };
                    }
                }

                // Try to get from sessionStorage
                const storedData = sessionStorage.getItem('titanSelectedStore');
                if (storedData) {
                    const parsed = JSON.parse(storedData);
                    const code = parsed.code || '';
                    if (code) {
                        return {
                            storeCode: code,
                            storeName: parsed.store_name || parsed.name || storeName || ''
                        };
                    }
                }

                // Try to get from URL parameter
                const urlParams = new URLSearchParams(window.location.search);
                const storeCode = urlParams.get('store');

                if (storeCode && storeCode.trim()) {
                    // Get store name from metaobjects
                    const metaobjectsScript = document.getElementById('titan-store-metaobjects');
                    if (metaobjectsScript) {
                        const metaobjects = JSON.parse(metaobjectsScript.textContent);
                        const store = metaobjects.find(function (s) {
                            const code = s.store_code || '';
                            return code === storeCode || code === storeCode.toUpperCase() || code === storeCode.toLowerCase();
                        });

                        if (store) {
                            return {
                                storeCode: storeCode.trim(),
                                storeName: store.store_name || storeName || ''
                            };
                        }
                    }

                    // Fallback: get store name from page
                    const storeNameEl = document.querySelector('[data-store-detail="title"]');
                    return {
                        storeCode: storeCode.trim(),
                        storeName: storeNameEl ? storeNameEl.textContent.trim() : storeName || ''
                    };
                }

                // Fallback: get from page element or use passed storeName
                const storeNameEl = document.querySelector('[data-store-detail="title"]');
                return {
                    storeCode: '',
                    storeName: storeNameEl ? storeNameEl.textContent.trim() : storeName || ''
                };
            } catch (error) {
                console.error('[Appointment Booking] Error getting store info:', error);
                return { storeCode: '', storeName: storeName || '' };
            }
        }

        // Helper function to split name into firstName and lastName
        function splitName(fullName) {
            if (!fullName || typeof fullName !== 'string') {
                return { firstName: '', lastName: '' };
            }

            const nameParts = fullName.trim().split(/\s+/);
            if (nameParts.length === 1) {
                return { firstName: nameParts[0], lastName: '' };
            } else {
                const firstName = nameParts[0];
                const lastName = nameParts.slice(1).join(' ');
                return { firstName, lastName };
            }
        }

        // Helper function to format appointment date/time
        function formatAppointmentDateTime(selectedDayData, selectedTime) {
            if (!selectedDayData || !selectedTime) {
                return '';
            }

            try {
                // Parse the selected time (format: "3:30 PM" or "15:30")
                const time12h = selectedTime.trim();
                let hour24, minute;

                // Check if it's 12-hour format (contains AM/PM)
                if (time12h.includes('AM') || time12h.includes('PM')) {
                    // Remove spaces and convert to uppercase for easier parsing
                    const timeUpper = time12h.toUpperCase().replace(/\s+/g, '');
                    const amIndex = timeUpper.indexOf('AM');
                    const pmIndex = timeUpper.indexOf('PM');
                    const periodIndex = amIndex !== -1 ? amIndex : pmIndex;

                    if (periodIndex !== -1) {
                        const timePart = timeUpper.substring(0, periodIndex);
                        const period = timeUpper.substring(periodIndex, periodIndex + 2);
                        const [hours, minutes] = timePart.split(':');
                        hour24 = parseInt(hours, 10);
                        minute = parseInt(minutes, 10) || 0;

                        if (period === 'PM' && hour24 !== 12) {
                            hour24 += 12;
                        } else if (period === 'AM' && hour24 === 12) {
                            hour24 = 0;
                        }
                    } else {
                        throw new Error('Could not parse AM/PM from time');
                    }
                } else {
                    // 24-hour format
                    const [hours, minutes] = time12h.split(':');
                    hour24 = parseInt(hours, 10);
                    minute = parseInt(minutes, 10) || 0;
                }

                // Validate parsed values
                if (isNaN(hour24) || isNaN(minute) || hour24 < 0 || hour24 > 23 || minute < 0 || minute > 59) {
                    throw new Error('Invalid time values: ' + hour24 + ':' + minute);
                }

                // Format date: YYYY-MM-DD HH:MM:SS
                const date = new Date(selectedDayData.date);
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                const hour = String(hour24).padStart(2, '0');
                const min = String(minute).padStart(2, '0');

                return `${year}-${month}-${day} ${hour}:${min}:00`;
            } catch (error) {
                console.error('[Appointment Booking] Error formatting date/time:', error);
                return '';
            }
        }

        // Book Now button (form submission)
        const bookNowBtn = document.getElementById('appointment-book-now');
        const bookingForm = document.getElementById('appointment-booking-form');
        if (bookNowBtn && bookingForm) {
            bookingForm.addEventListener('submit', async function (e) {
                e.preventDefault();
                e.stopPropagation();


                // Get form values - use form.querySelector to ensure we get the checkbox from the correct form
                const nameInput = bookingForm.querySelector('#appointment-name');
                const phoneInput = bookingForm.querySelector('#appointment-phone');
                const emailInput = bookingForm.querySelector('#appointment-email');
                const consentCheckbox = bookingForm.querySelector('#appointment-consent');


                // Validate form
                if (!nameInput || !nameInput.value.trim()) {
                    alert('Please enter your name');
                    nameInput?.focus();
                    return;
                }

                if (!phoneInput || !phoneInput.value.trim()) {
                    alert('Please enter your phone number');
                    phoneInput?.focus();
                    return;
                }

                if (!emailInput || !emailInput.value.trim()) {
                    alert('Please enter your email address');
                    emailInput?.focus();
                    return;
                }

                // Check consent checkbox - try multiple ways to find it
                let actualConsentCheckbox = consentCheckbox;

                // If not found in form, try to find it in the modal
                if (!actualConsentCheckbox) {
                    const modal = document.getElementById('store-appointment-modal');
                    if (modal) {
                        actualConsentCheckbox = modal.querySelector('#appointment-consent');
                    }
                }

                // Final check - ensure we have the checkbox and it's checked
                if (!actualConsentCheckbox) {
                    alert('Please agree to the consent checkbox to proceed.');
                    return;
                }

                if (!actualConsentCheckbox.checked) {
                    alert('Please agree to the consent checkbox to proceed.');
                    actualConsentCheckbox.focus();
                    return;
                }

                if (!selectedDayData || !selectedTime) {
                    alert('Please select a day and time for your appointment.');
                    return;
                }

                // Get store information
                const storeInfo = getStoreInfo();

                // Validate store code is present
                if (!storeInfo.storeCode || !storeInfo.storeCode.trim()) {
                    alert('Store information is missing. Please try booking from the store detail page or refresh the page and try again.');
                    return;
                }

                // Split name into firstName and lastName
                const fullName = nameInput.value.trim();
                const { firstName, lastName } = splitName(fullName);

                // Ensure lastName is not empty
                const finalFirstName = firstName || fullName;
                const finalLastName = lastName || (firstName || fullName);

                // Format phone number - remove prefix if present
                let phoneNumber = phoneInput.value.trim().replace(/\s+/g, '');
                if (phoneNumber.startsWith('+971')) {
                    phoneNumber = phoneNumber.substring(4);
                } else if (phoneNumber.startsWith('971')) {
                    phoneNumber = phoneNumber.substring(3);
                }

                // Format appointment date/time
                const appointmentDateTime = formatAppointmentDateTime(selectedDayData, selectedTime);
                if (!appointmentDateTime) {
                    alert('Error formatting appointment date/time. Please try again.');
                    return;
                }

                // Validate required fields
                if (!finalFirstName || !finalFirstName.trim()) {
                    alert('Please enter your name');
                    nameInput?.focus();
                    return;
                }

                if (!phoneNumber || !phoneNumber.trim()) {
                    alert('Please enter a valid phone number');
                    phoneInput?.focus();
                    return;
                }

                if (!emailInput.value.trim() || !emailInput.value.includes('@')) {
                    alert('Please enter a valid email address');
                    emailInput?.focus();
                    return;
                }

                // Prepare API payload
                const payload = {
                    firstName: finalFirstName.trim(),
                    lastName: finalLastName.trim() || finalFirstName.trim(), // Ensure lastName is not empty
                    email: emailInput.value.trim(),
                    phone: phoneNumber.trim(),
                    brand: 'Wot',
                    storeCode: (storeInfo.storeCode || '').trim(),
                    skuUrl: '',
                    ticketType: 'Titan UAE website Lead',
                    country: 'UAE',
                    AppointmentDate: appointmentDateTime,
                    storeName: (storeInfo.storeName || storeName || '').trim(),
                    channel: 'Titan UAE Online Store'
                };


                // Disable submit button and show loading state
                if (bookNowBtn) {
                    bookNowBtn.disabled = true;
                    const originalText = bookNowBtn.querySelector('span')?.textContent || 'Book Now';
                    const spanElement = bookNowBtn.querySelector('span');
                    if (spanElement) {
                        spanElement.textContent = 'Booking...';
                    }

                    try {
                        // Sheet url (https://docs.google.com/spreadsheets/d/1Jh98i2t8VCy8HD8GjzO2073de-lH9sOjekCV_2VfY_U/edit?gid=0#gid=0)
                        let url = 'https://script.google.com/macros/s/AKfycbxZTPChW8swHQzOreoY8BN41axoL1P_ngeB0K4mOU0PNiBSFuogGnGLPw4SW3_K-Rzm/exec';
                        const res = await fetch(`${url}?action=appointmentFormForPost`, {
                            method: 'POST',
                            mode: 'no-cors',
                            body: JSON.stringify(payload)
                        });

                        const data = await res.json();
                    }
                    catch (error) {
                        console.error('Not submitted to appointment data', error);
                    }

                    try {
                        // Send to proxy endpoint
                        const proxyUrl = '/apps/sms/api/external/book-appointment';

                        const response = await fetch(proxyUrl, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json'
                            },
                            body: JSON.stringify(payload)
                        });

                        let responseData;
                        let responseText = '';

                        try {
                            responseText = await response.text();

                            if (responseText) {
                                try {
                                    responseData = JSON.parse(responseText);
                                } catch (parseError) {
                                    console.error('[Appointment Booking] Failed to parse JSON response:', parseError);
                                    responseData = { message: responseText || 'Invalid response from server' };
                                }
                            } else {
                                responseData = { message: 'Empty response from server' };
                            }
                        } catch (error) {
                            console.error('[Appointment Booking] Error reading response:', error);
                            responseData = { message: 'Failed to read response from server' };
                        }


                        // Helper function to safely convert to string
                        function safeStringify(value) {
                            if (typeof value === 'string') {
                                return value;
                            }
                            if (typeof value === 'object' && value !== null) {
                                try {
                                    return JSON.stringify(value);
                                } catch (e) {
                                    return String(value);
                                }
                            }
                            return String(value || '');
                        }

                        if (response.ok && responseData && responseData.responseCode === 0 && responseData.status === 'success') {
                            let leadCity = '';
                            try {
                                const storedData = JSON.parse(sessionStorage.getItem('titanSelectedStore') || '{}');
                                leadCity = storedData.emirate || storedData.city || storedData.country || '';
                            } catch (error) {
                                leadCity = '';
                            }

                            if (window.pushGtmEvent) {
                                window.pushGtmEvent({
                                    event: 'generate_lead',
                                    page_url: window.location.href,
                                    city: leadCity,
                                    store_name: payload.storeName || '',
                                    date_time: appointmentDateTime,
                                });
                            }

                            // Send OneTrust consent if checkbox is checked
                            // Get the checkbox again to ensure we have the correct reference
                            let currentConsentCheckbox = bookingForm.querySelector('#appointment-consent');
                            if (!currentConsentCheckbox) {
                                const modal = document.getElementById('store-appointment-modal');
                                if (modal) {
                                    currentConsentCheckbox = modal.querySelector('#appointment-consent');
                                }
                            }

                            if (currentConsentCheckbox && currentConsentCheckbox.checked) {
                                const email = emailInput.value.trim();
                                const phoneNumber = phoneInput.value.trim();
                                if (window.sendAppointmentConsent) {
                                    await window.sendAppointmentConsent(email, phoneNumber);
                                }
                            }

                            // Success
                            const successMessage = safeStringify(responseData.message || 'Appointment booked successfully!');
                            if (responseData.data && responseData.data['SF Case Details']) {
                                const caseId = safeStringify(responseData.data['SF Case Details']);

                            }
                            // Close modal
                            let successMsg = document.querySelector('#appointment-msg-wrapper');
                            if (successMsg) {
                                successMsg.style.display = 'block';
                                bookingForm.reset();
                                setTimeout(() => {
                                    successMsg.style.display = 'none';
                                }, 3000);
                            }
                            setTimeout(() => {
                                closeModal();
                            }, 5000);
                        } else {
                            // Error response
                            let errorMessage = 'Failed to book appointment. Please try again.';

                            if (responseData) {
                                if (responseData.errors && Array.isArray(responseData.errors) && responseData.errors.length > 0) {
                                    errorMessage = safeStringify(responseData.errors[0]) || errorMessage;
                                } else if (responseData.message) {
                                    errorMessage = safeStringify(responseData.message);
                                } else if (responseData.error) {
                                    errorMessage = safeStringify(responseData.error);
                                } else if (response.status === 400) {
                                    errorMessage = 'Invalid request. Please check your information and try again.';
                                    if (responseText) {
                                        console.error('[Appointment Booking] 400 Bad Request response:', responseText);
                                    }
                                }
                            }

                            console.error('[Appointment Booking] Submission failed:', {
                                status: response.status,
                                statusText: response.statusText,
                                responseData: responseData,
                                responseText: responseText
                            });

                            // alert(errorMessage);
                        }
                    } catch (error) {
                        alert('An error occurred while booking your appointment. Please try again later.');
                    } finally {
                        // Re-enable submit button
                        if (bookNowBtn) {
                            bookNowBtn.disabled = false;
                            const spanElement = bookNowBtn.querySelector('span');
                            if (spanElement) {
                                spanElement.textContent = originalText;
                            }
                        }
                    }
                }
            });
        }

        // Close button
        const closeBtn = modal.querySelector('.store-appointment-modal-close');
        const overlay = modal.querySelector('.store-appointment-modal-overlay');

        function closeModal() {
            modal.style.display = 'none';
            // Restore body scroll when modal is closed
            document.body.style.overflow = '';
            setTimeout(() => modal.remove(), 300);
        }

        if (closeBtn) {
            closeBtn.addEventListener('click', closeModal);
        }

        if (overlay) {
            overlay.addEventListener('click', closeModal);
        }

        // Close on Escape key
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                closeModal();
                document.removeEventListener('keydown', handleEscape);
            }
        };
        document.addEventListener('keydown', handleEscape);

        // Modal should already be shown in the try block above
        // But ensure it's visible as fallback
        if (modal.style.display !== 'flex') {
            modal.style.display = 'flex';
        }
    }

    // Initialize when DOM is ready - multiple fallbacks to ensure it runs
    function initialize() {
        try {
            initAppointmentBooking();
        } catch (error) {
            console.error('[Appointment Booking] Error during initialization:', error);
        }
    }

    // Try multiple initialization methods
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initialize);
    } else {
        // DOM already loaded, run immediately
        initialize();
    }

    // Also try after a short delay as fallback
    setTimeout(function () {
        if (typeof initAppointmentBooking === 'function') {
            try {
                initAppointmentBooking();
            } catch (error) {
                console.error('[Appointment Booking] Fallback init error:', error);
            }
        }
    }, 1000);

    // OneTrust Consent API Integration - Send consent only when appointment consent checkbox is checked
    (function () {
        const API_URL = 'https://privacyportal-in.onetrust.com/request/v1/consentreceipts';
        // Using the same API token from store-detail.liquid
        const API_TOKEN = 'eyJhbGciOiJSUzUxMiJ9.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.JcoLTrdexwHUBeOJ3Eph3-nGtAj1Ue267MPoQidWYChDej2RuIuNbV7BP4O2yXWyKNr9g_JU2Xp7nz2Ry0hEry7AE7tYLx1Ut23IWyeThY35fxTEodZT3PYbEqFKxMET6Ul1bJHkND-H8BqrX_doruE7eX07ia6DDM4sR-TFxzBQRNELXnhAX4bDTvYi7viJai56hXCtLcYCrUKlr3nkMFtlJM0RFiBMEHsaGaV8Hk31oe8ZPhAXzC-7_e-N6fFky6ss2H688MpJzTCWo7arY9D_DS-axI0VDm_JDcnW1pQaUPj7e22C68n9fW5PtPo6LLYo6xoAZjgMkyCk05r1v1z7YfC7gw7yIIL57aaUEY48GFqiD505Pwe-QkN1ulMqlh7PPbeLwBcVKGYgYe_9HHEbTxOKSOpICLJRNxRw5ZU4Vx6V_078WgSPrZbxk0bHrl4we02QQMUisOSwRJrzZynsejvpcHthWkjJ1dfu0qBRWnVqNGfz3OyNjfq4hjLE4qjWiziJbHW7qqNfH375Hpp34xh1OZRVuPBgy9y4HIr_y7ufpY33LM8BpI6szT6yTJzOFNY1UvJwZij343XkM2-e0bitSYc7ywZSpeQI1Eqhx4VzOBK6R0cCj0TjQg05TA-kxMrLPxuYAdrhxx6EfagKnIv2WXLNSscVnocKHmk';
        const APPOINTMENT_PURPOSE_ID = 'ba4bea97-40de-4afb-a63f-aaec65039ab4';

        // Function to send appointment consent
        async function sendAppointmentConsent(email, phoneNumber) {
            // If no email or phone, we can't send consent
            if (!email && !phoneNumber) {
                console.warn('[OneTrust Appointment Consent] No identifier (email or phone) available');
                return;
            }

            // Format phone number - remove prefix if present
            let formattedPhone = phoneNumber || '';
            if (formattedPhone.startsWith('+971')) {
                formattedPhone = formattedPhone.substring(4);
            } else if (formattedPhone.startsWith('971')) {
                formattedPhone = formattedPhone.substring(3);
            }
            // Remove any spaces
            formattedPhone = formattedPhone.replace(/\s+/g, '');

            const payload = {
                identifier: email || formattedPhone || '',
                requestInformation: API_TOKEN,
                purposes: [
                    {
                        Id: APPOINTMENT_PURPOSE_ID,
                        TransactionType: 'CONFIRMED'
                    }
                ],
                additionalIdentifiers: {
                    'Registered Mobile Number': formattedPhone || ''
                }
            };

            try {
                const response = await fetch(API_URL, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(payload)
                });

                if (!response.ok) {
                    const errorText = await response.text();
                    console.error('[OneTrust Appointment Consent] API Error:', response.status, errorText);
                } else {
                    const responseData = await response.json();
                }
            } catch (error) {
                console.error('[OneTrust Appointment Consent] Error sending consent:', error);
            }
        }

        // Export function to be used in appointment booking form submission handler
        window.sendAppointmentConsent = sendAppointmentConsent;
    })();

})();

